import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, SkipBack, SkipForward, Users } from 'lucide-react';

// Store a global instance of AudioContext to be reused
let globalAudioContext = null;

// Store global connection information
const videoSourceMap = new WeakMap();

// Debug flag - set to true to enable detailed logging
const DEBUG = true;

export const VideoPlayer = ({ 
  src, 
  onError,
  selectedJudgeIndex = null, // Index of the currently selected judge (0, 1, 2, etc.)
  judgesCount = 3,          // Default number of judges/audio channels
  allChannelsMode = false   // Option to listen to all channels at once
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAudioInitialized, setIsAudioInitialized] = useState(false);
  const [audioChannelsDetected, setAudioChannelsDetected] = useState(0);
  const [audioError, setAudioError] = useState(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(null);
  
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const mediaStreamSourceRef = useRef(null);
  const gainNodesRef = useRef([]);
  const analyserNodesRef = useRef([]);
  const setupTimeoutRef = useRef(null);
  const audioProcessingActiveRef = useRef(false);

  // Clean up audio processing
  const cleanupAudio = useCallback(() => {
    if (DEBUG) console.log("Cleaning up audio processing");
    
    // Reset state
    setIsAudioInitialized(false);
    audioProcessingActiveRef.current = false;
    
    // Clean up gain nodes
    if (gainNodesRef.current.length > 0) {
      gainNodesRef.current.forEach(node => {
        try {
          if (node) node.disconnect();
        } catch (e) {}
      });
      gainNodesRef.current = [];
    }
    
    // Clean up analyser nodes
    if (analyserNodesRef.current.length > 0) {
      analyserNodesRef.current.forEach(node => {
        try {
          if (node) node.disconnect();
        } catch (e) {}
      });
      analyserNodesRef.current = [];
    }

    // Note: We do NOT disconnect the mediaStreamSource or close the audioContext
    // as this would create issues with reconnection
  }, []);

  // Initialize or reset audio context when src changes
  useEffect(() => {
    if (DEBUG) console.log("Source changed, reinitializing audio:", src);
    
    // Reset video state
    setVideoLoaded(false);
    setVideoError(null);
    
    if (videoRef.current) {
      // Clean up any existing audio processing
      cleanupAudio();
      
      // Clear any pending setup attempts
      if (setupTimeoutRef.current) {
        clearTimeout(setupTimeoutRef.current);
      }
      
      // Load new video
      videoRef.current.load();
      
      // Log audio tracks from the video element (HTML5 audio tracks API)
      const checkAudioTracks = () => {
        try {
          if (videoRef.current.audioTracks) {
            console.log("HTML5 Audio Tracks API reports:", videoRef.current.audioTracks.length, "tracks");
            for (let i = 0; i < videoRef.current.audioTracks.length; i++) {
              console.log(`Track ${i}:`, videoRef.current.audioTracks[i]);
            }
          } else {
            console.log("HTML5 Audio Tracks API not supported in this browser");
          }
        } catch (e) {
          console.error("Error checking audio tracks:", e);
        }
      };
      
      // Check once metadata is loaded
      videoRef.current.addEventListener('loadedmetadata', checkAudioTracks, { once: true });
      
      // Set up audio processing after a delay
      setupTimeoutRef.current = setTimeout(() => {
        setupAudioProcessing();
      }, 300);
    }
    
    return () => {
      // Clean up on unmount or source change
      cleanupAudio();
      
      if (setupTimeoutRef.current) {
        clearTimeout(setupTimeoutRef.current);
      }
    };
  }, [src, cleanupAudio]);

  // Effect to switch audio channels when selectedJudgeIndex changes
  useEffect(() => {
    if (isAudioInitialized && gainNodesRef.current.length > 0) {
      if (DEBUG) console.log("Updating active audio channel:", selectedJudgeIndex, "All channels mode:", allChannelsMode);
      updateActiveAudioChannel();
    }
  }, [selectedJudgeIndex, allChannelsMode, isAudioInitialized]);

  // Function to analyze the number of audio channels in the video
  const analyzeAudioChannels = useCallback(() => {
    if (!videoRef.current || !mediaStreamSourceRef.current || !globalAudioContext) return 0;
    
    try {
      // Try multiple methods to detect channel count
      
      // Method 1: Direct channel count from MediaElementSource
      const sourceChannelCount = mediaStreamSourceRef.current.channelCount;
      console.log("Method 1 - Source channelCount:", sourceChannelCount);
      
      // Method 2: Get channel count from audio element
      const videoChannelCount = videoRef.current.mozChannelCount || // Firefox
                               videoRef.current.webkitAudioChannelCount || // Safari
                               videoRef.current.audioChannelCount || // Standard (future)
                               (videoRef.current.audioTracks ? videoRef.current.audioTracks.length : 0); // HTML5 Audio Tracks API
      console.log("Method 2 - Video element channel indicators:", videoChannelCount);
      
      // Method 3: Try to access the raw audio buffer (if available)
      let bufferChannelCount = 0;
      try {
        if (mediaStreamSourceRef.current.buffer) {
          bufferChannelCount = mediaStreamSourceRef.current.buffer.numberOfChannels;
          console.log("Method 3 - Buffer numberOfChannels:", bufferChannelCount);
        }
      } catch (e) {
        console.log("Method 3 not applicable - no buffer access");
      }
      
      // Choose the maximum detected channel count, with fallback to judgesCount
      const detectedChannelCount = Math.max(
        sourceChannelCount, 
        videoChannelCount, 
        bufferChannelCount
      );
      
      // If we detected 0 or a ridiculously high number, fall back to a reasonable value
      const finalChannelCount = (detectedChannelCount > 0 && detectedChannelCount < 10) 
        ? detectedChannelCount 
        : Math.min(judgesCount, 3); // Fallback to reasonable default
      
      console.log("Final detected audio channels:", finalChannelCount);
      setAudioChannelsDetected(finalChannelCount);
      
      // For MOV files specifically, try to use requested judgesCount if it's 3
      // This is because MOV files often have their channels correctly encoded but browsers might not report properly
      if (src && src.toLowerCase().endsWith('.mov') && judgesCount === 3) {
        console.log("MOV file detected with 3 judges - forcing 3 channel mode");
        return 3; // Force 3 channels for MOV files when 3 judges are requested
      }
      
      return finalChannelCount;
    } catch (e) {
      console.error("Error analyzing audio channels:", e);
      setAudioError("Error analyzing audio channels: " + e.message);
      return judgesCount; // Fallback to judgesCount on error
    }
  }, [judgesCount, src]);

  // Setup Web Audio API for channel isolation
  const setupAudioProcessing = useCallback(() => {
    if (!videoRef.current || audioProcessingActiveRef.current) {
      return;
    }
    
    console.log("Setting up audio processing...");
    audioProcessingActiveRef.current = true;
    
    try {
      // Get or create the global audio context
      if (!globalAudioContext) {
        globalAudioContext = new (window.AudioContext || window.webkitAudioContext)();
        console.log("Created new global AudioContext");
      }
      
      // Function to initialize the audio processing
      const initializeAudio = () => {
        if (isAudioInitialized) return;
        
        console.log("Initializing audio nodes");
        
        try {
          // Check if this video element already has a MediaElementSource
          if (videoSourceMap.has(videoRef.current)) {
            console.log("Reusing existing MediaElementSource");
            mediaStreamSourceRef.current = videoSourceMap.get(videoRef.current);
          } else {
            console.log("Creating new MediaElementSource");
            // Create a new MediaElementSource and store it
            mediaStreamSourceRef.current = globalAudioContext.createMediaElementSource(videoRef.current);
            videoSourceMap.set(videoRef.current, mediaStreamSourceRef.current);
          }
          
          // Analyze the number of audio channels
          const availableChannels = analyzeAudioChannels();
          
          // For MOV files with 3 judges, force 3 channels
          const effectiveChannels = (src && src.toLowerCase().endsWith('.mov') && judgesCount === 3) 
            ? 3 
            : availableChannels;
          
          // If we don't have enough channels, we'll need to adapt
          const effectiveJudgesCount = Math.min(judgesCount, Math.max(3, effectiveChannels));
          console.log(`Setting up audio processing with ${effectiveChannels} available channels for ${judgesCount} judges`);
          
          // Create a channel splitter to separate the audio channels
          // Use the actual available channel count to prevent errors
          const splitter = globalAudioContext.createChannelSplitter(Math.max(3, effectiveChannels));
          
          // Connect the source to the splitter
          mediaStreamSourceRef.current.connect(splitter);
          
          // Create gain nodes for each judge (even if we have fewer channels)
          gainNodesRef.current = Array(judgesCount).fill().map(() => globalAudioContext.createGain());
          analyserNodesRef.current = Array(judgesCount).fill().map(() => {
            const analyser = globalAudioContext.createAnalyser();
            analyser.fftSize = 256;
            return analyser;
          });
          
          // Create a merger to combine the channels back to stereo
          const merger = globalAudioContext.createChannelMerger(2);
          
          // Connect each channel from the splitter to its own gain node and then to the merger
          // If we have more judges than channels, we'll reuse channels
          for (let i = 0; i < judgesCount; i++) {
            if (DEBUG) console.log(`Setting up channel for judge ${i}`);
            
            // Determine which audio channel to use (cycle through available channels if needed)
            // Force MOV files with 3 judges to use channels 0, 1, 2
            const sourceChannelIndex = i % Math.max(3, effectiveChannels);
            if (DEBUG) console.log(`Judge ${i} using audio channel ${sourceChannelIndex}`);
            
            // Connect channel from splitter to gain node
            splitter.connect(gainNodesRef.current[i], sourceChannelIndex);
            
            // Connect gain node to analyzer
            gainNodesRef.current[i].connect(analyserNodesRef.current[i]);
            
            // Connect analyzer to both left and right channels of the merger
            // This ensures mono channels are heard in both ears
            analyserNodesRef.current[i].connect(merger, 0, 0); // Left
            analyserNodesRef.current[i].connect(merger, 0, 1); // Right
          }
          
          // Connect merger to destination (speakers)
          merger.connect(globalAudioContext.destination);
          
          // Set the initial state of gain nodes based on selected judge
          updateActiveAudioChannel();
          
          setIsAudioInitialized(true);
          console.log("Audio processing initialized successfully");
        } catch (error) {
          console.error("Error in audio initialization:", error);
          setAudioError("Error initializing audio: " + error.message);
          audioProcessingActiveRef.current = false;
        }
      };

      // Initialize once metadata is loaded
      if (videoRef.current.readyState >= 1) {
        initializeAudio();
      } else {
        videoRef.current.addEventListener('loadedmetadata', initializeAudio, { once: true });
      }
    } catch (error) {
      console.error("Error setting up audio processing:", error);
      setAudioError("Error setting up audio: " + error.message);
      audioProcessingActiveRef.current = false;
    }
  }, [judgesCount, isAudioInitialized, analyzeAudioChannels, src]);

  // Function to monitor audio levels on each channel (for debugging)
  const monitorChannelLevels = (channelIndex) => {
    if (!analyserNodesRef.current[channelIndex]) return;
    
    // Only monitor if in debug mode
    if (!DEBUG) return;
    
    const analyser = analyserNodesRef.current[channelIndex];
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    
    const checkLevel = () => {
      if (!analyserNodesRef.current[channelIndex]) return;
      
      analyser.getByteFrequencyData(dataArray);
      
      // Calculate average level
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i];
      }
      const average = sum / dataArray.length;
      
      // Only log significant changes to reduce console spam
      if (average > 20) {
        console.log(`Judge ${channelIndex} audio level: ${average.toFixed(2)}`);
      }
      
      // Continue monitoring if this channel is selected or all channels mode is on
      if (channelIndex === selectedJudgeIndex || allChannelsMode) {
        requestAnimationFrame(checkLevel);
      }
    };
    
    // Start monitoring
    requestAnimationFrame(checkLevel);
  };

  // Update which audio channels are active based on selectedJudgeIndex
  const updateActiveAudioChannel = useCallback(() => {
    if (!gainNodesRef.current.length) return;
    
    if (DEBUG) console.log(`${allChannelsMode ? 'All channels mode' : 'Single channel mode'}, selected: ${selectedJudgeIndex}`);
    
    // If in "all channels" mode, set all channels active
    if (allChannelsMode) {
      gainNodesRef.current.forEach((node, i) => {
        node.gain.value = 1.0;
        if (DEBUG) console.log(`Setting judge ${i} gain to 1.0`);
      });
      return;
    }
    
    // Otherwise, isolate just the selected judge's channel
    gainNodesRef.current.forEach((node, index) => {
      const value = index === selectedJudgeIndex ? 1.0 : 0.0;
      node.gain.value = value;
      if (DEBUG) console.log(`Setting judge ${index} gain to ${value}`);
    });
  }, [selectedJudgeIndex, allChannelsMode]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        // Resume AudioContext if it was suspended
        if (globalAudioContext && globalAudioContext.state === 'suspended') {
          globalAudioContext.resume();
        }
        videoRef.current.play().catch(err => {
          console.error("Error playing video:", err);
          setVideoError("Playback error: " + err.message);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      // Unlike standard mute which affects all channels,
      // we'll adjust all gain nodes to zero or back to their proper values
      const newMuteState = !isMuted;
      if (newMuteState) {
        // Store current gains and set all to zero
        gainNodesRef.current.forEach(node => {
          node.gain.value = 0;
        });
      } else {
        // Restore proper gain values
        updateActiveAudioChannel();
      }
      setIsMuted(newMuteState);
    }
  };

  const seek = (amount) => {
    if (videoRef.current) {
      videoRef.current.currentTime += amount;
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      console.log("Video metadata loaded. Duration:", videoRef.current.duration);
    }
  };
  
  const handleLoadedData = () => {
    setVideoLoaded(true);
    console.log("Video data loaded and ready to play");
  };
  
  const handleVideoError = (e) => {
    console.error("Video element error:", e);
    const error = e.target.error;
    let errorMessage = "Unknown video error";
    
    if (error) {
      switch (error.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          errorMessage = "You aborted the video playback";
          break;
        case MediaError.MEDIA_ERR_NETWORK:
          errorMessage = "A network error caused the video download to fail";
          break;
        case MediaError.MEDIA_ERR_DECODE:
          errorMessage = "The video playback was aborted due to a corruption problem";
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMessage = "The video format is not supported by your browser";
          break;
        default:
          errorMessage = `Error code: ${error.code}`;
      }
    }
    
    setVideoError(errorMessage);
    if (onError) onError(errorMessage);
  };

  const handleProgressChange = (e) => {
    const time = parseFloat(e.target.value);
    setCurrentTime(time);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Determine if this is a MOV file
  const isMovFile = src && src.toLowerCase().endsWith('.mov');

  return (
    <div 
      ref={containerRef}
      className="relative group bg-base-300 rounded-lg overflow-hidden aspect-video"
    >
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onLoadedData={handleLoadedData}
        onError={handleVideoError}
        src={src}
        // Important: Remove the controls attribute to use our custom controls
        // Add playsInline and muted (initially) to improve playback on some browsers
        crossOrigin="anonymous"
        playsInline
      />

      {/* Audio status indicator */}
      <div className="absolute top-4 right-4 bg-black/50 text-white text-xs px-2 py-1 rounded">
        Channels: {audioChannelsDetected} | Selected: Judge {selectedJudgeIndex + 1}
        {audioChannelsDetected < judgesCount && (
          <div className="text-yellow-400 mt-1 text-[10px]">
            {isMovFile ? 
              "Using 3-channel mode for MOV file" :
              `Warning: Audio channels (${audioChannelsDetected}) < Judges (${judgesCount})`
            }
          </div>
        )}
        {audioError && (
          <div className="text-red-400 mt-1 text-[10px]">
            Error: {audioError}
          </div>
        )}
      </div>

      {/* Video Error Display */}
      {videoError && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-900/80 text-white p-4 rounded-lg max-w-[80%] text-center">
          <h3 className="font-bold mb-2">Video Error</h3>
          <p>{videoError}</p>
        </div>
      )}

      {/* Loading Indicator */}
      {!videoLoaded && !videoError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
        </div>
      )}

      {/* Video Controls */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 transition-opacity duration-300 opacity-0 group-hover:opacity-100">
        {/* Progress Bar */}
        <input
          type="range"
          min={0}
          max={duration || 0}
          value={currentTime}
          onChange={handleProgressChange}
          className="range range-xs range-primary w-full"
        />

        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center space-x-2">
            {/* Play/Pause */}
            <button 
              onClick={togglePlay}
              className="btn btn-circle btn-sm btn-ghost text-white"
            >
              {isPlaying ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
            </button>

            {/* Skip Buttons */}
            <button 
              onClick={() => seek(-10)}
              className="btn btn-circle btn-sm btn-ghost text-white"
            >
              <SkipBack className="w-4 h-4" />
            </button>
            <button 
              onClick={() => seek(10)}
              className="btn btn-circle btn-sm btn-ghost text-white"
            >
              <SkipForward className="w-4 h-4" />
            </button>

            {/* Time */}
            <span className="text-sm text-white">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            {/* Volume */}
            <button 
              onClick={toggleMute}
              className="btn btn-circle btn-sm btn-ghost text-white"
            >
              {isMuted ? (
                <VolumeX className="w-4 h-4" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>

            {/* Fullscreen */}
            <button 
              onClick={toggleFullscreen}
              className="btn btn-circle btn-sm btn-ghost text-white"
            >
              <Maximize className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Play Button Overlay */}
      {!isPlaying && videoLoaded && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
        >
          <Play className="w-16 h-16 text-white" />
        </button>
      )}
    </div>
  );
};